import React, { useState } from 'react';
import { ChevronDown, Film, FolderOpen, LayoutGrid, Plus, Trash2, Users } from 'lucide-react';
import type { CanvasProjectMeta } from '../../lib/canvasBoard';
import type { SharedWithMe } from '../../lib/canvasShare';

const COLLAPSED_KEY = 'seu.video.telaEmBranco.projectsCollapsed';

type Props = {
  projects: CanvasProjectMeta[];
  /** Projects other people of the company shared with the user. */
  shared: SharedWithMe[];
  activeId: string;
  /** Something is generating: switching now would send results to the wrong project. */
  busy: boolean;
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
  onOpen: (id: string) => void;
  onOpenShared: (id: string) => void;
  onNew: () => void;
  onRename: (id: string, title: string) => void;
  onDelete: (id: string) => void;
};

export function loadProjectsCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

export function saveProjectsCollapsed(collapsed: boolean): void {
  try {
    localStorage.setItem(COLLAPSED_KEY, collapsed ? '1' : '0');
  } catch {
    /* storage unavailable */
  }
}

function relativeTime(ts: number): string {
  const diff = Math.max(0, Date.now() - ts);
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'agora';
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h} h`;
  const d = Math.floor(h / 24);
  if (d === 1) return 'ontem';
  if (d < 7) return `há ${d} dias`;
  return new Date(ts).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
}

function metaLine(p: CanvasProjectMeta): string {
  const parts: string[] = [];
  if (p.scenes) parts.push(`${p.scenes} ${p.scenes === 1 ? 'cena' : 'cenas'}`);
  if (p.videos) parts.push(`${p.videos} ${p.videos === 1 ? 'vídeo' : 'vídeos'}`);
  if (!parts.length) parts.push('vazio');
  parts.push(relativeTime(p.updatedAt));
  return parts.join(' · ');
}

function Thumb({ url, kind }: { url?: string; kind?: 'image' | 'video' }) {
  return (
    <span className="tb-proj-thumb">
      {url && kind === 'image' ? (
        <img src={url} alt="" loading="lazy" draggable={false} />
      ) : url ? (
        <>
          <video src={`${url}#t=0.5`} muted playsInline preload="metadata" />
          <span className="tb-tray-badge"><Film size={10} /></span>
        </>
      ) : (
        <LayoutGrid size={14} />
      )}
    </span>
  );
}

export default function CanvasProjectsPanel(props: Props) {
  const { projects, shared, activeId, busy, collapsed } = props;
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const sorted = [...projects].sort((a, b) => b.updatedAt - a.updatedAt);

  if (collapsed) {
    return (
      <button
        type="button"
        className="tb-tray-pill"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={() => props.onCollapsedChange(false)}
      >
        <FolderOpen size={15} /> Projetos
        <span className="tb-proj-count">{projects.length + shared.length}</span>
      </button>
    );
  }

  const commitRename = (id: string) => {
    const title = draft.replace(/\s+/g, ' ').trim();
    if (title) props.onRename(id, title.slice(0, 60));
    setEditingId(null);
  };

  return (
    <div className="tb-tray tb-proj" onPointerDown={(e) => e.stopPropagation()}>
      <div className="tb-tray-head">
        <strong>Projetos</strong>
        <span className="tb-proj-count">{projects.length + shared.length}</span>
        <button
          type="button"
          className="tb-proj-new"
          onClick={props.onNew}
          disabled={busy}
          title={busy ? 'Aguarde a geração terminar' : 'Começar um projeto novo'}
        >
          <Plus size={13} /> Novo
        </button>
        <button type="button" className="tb-tray-collapse" aria-label="Recolher" title="Recolher" onClick={() => props.onCollapsedChange(true)}>
          <ChevronDown size={15} />
        </button>
      </div>
      <div className="tb-tray-hint">
        {busy ? 'Aguarde a geração terminar para trocar de projeto.' : 'clique para abrir · 2 cliques no nome renomeia'}
      </div>

      <div className="tb-proj-list" role="list">
        {sorted.map((p) => {
          const active = p.id === activeId;
          const confirming = confirmId === p.id;
          return (
            <div key={p.id} role="listitem" className={`tb-proj-row${active ? ' is-active' : ''}`}>
              <button
                type="button"
                className="tb-proj-open"
                disabled={busy && !active}
                onClick={() => !active && props.onOpen(p.id)}
                title={active ? 'Projeto aberto' : `Abrir “${p.title}”`}
              >
                <Thumb url={p.thumbUrl} kind={p.thumbKind} />
                <span className="tb-proj-text">
                  {editingId === p.id ? (
                    <input
                      className="tb-proj-input"
                      value={draft}
                      autoFocus
                      maxLength={60}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => setDraft(e.target.value)}
                      onBlur={() => commitRename(p.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') commitRename(p.id);
                        if (e.key === 'Escape') setEditingId(null);
                      }}
                    />
                  ) : (
                    <span
                      className="tb-proj-title"
                      onDoubleClick={(e) => {
                        e.stopPropagation();
                        setDraft(p.title);
                        setEditingId(p.id);
                      }}
                    >
                      {p.title}
                    </span>
                  )}
                  <span className="tb-proj-meta">
                    {active && <span className="tb-proj-badge">Aberto</span>}
                    {p.shared && (
                      <span className="tb-proj-badge is-shared" title="Compartilhado com a equipe">
                        <Users size={10} />
                      </span>
                    )}
                    {metaLine(p)}
                  </span>
                </span>
              </button>
              {confirming ? (
                <span className="tb-proj-confirm">
                  <button type="button" className="is-danger" onClick={() => { setConfirmId(null); props.onDelete(p.id); }}>
                    Excluir
                  </button>
                  <button type="button" onClick={() => setConfirmId(null)}>Não</button>
                </span>
              ) : (
                <button
                  type="button"
                  className="tb-proj-del"
                  aria-label={`Excluir “${p.title}”`}
                  title="Excluir projeto"
                  disabled={busy && active}
                  onClick={() => setConfirmId(p.id)}
                >
                  <Trash2 size={13} />
                </button>
              )}
            </div>
          );
        })}
        {shared.length > 0 && (
          <>
            <div className="tb-proj-section"><Users size={12} /> Compartilhados comigo</div>
            {[...shared].sort((a, b) => b.updatedAt - a.updatedAt).map((s) => {
              const active = s.id === activeId;
              return (
                <div key={s.id} role="listitem" className={`tb-proj-row${active ? ' is-active' : ''}`}>
                  <button
                    type="button"
                    className="tb-proj-open"
                    disabled={busy && !active}
                    onClick={() => !active && props.onOpenShared(s.id)}
                    title={active ? 'Projeto aberto' : `Abrir “${s.title}”, de ${s.ownerName}`}
                  >
                    <Thumb url={s.thumbUrl} kind={s.thumbKind} />
                    <span className="tb-proj-text">
                      <span className="tb-proj-title">{s.title}</span>
                      <span className="tb-proj-meta">
                        {active && <span className="tb-proj-badge">Aberto</span>}
                        de {s.ownerName} · {s.role === 'edit' ? 'pode editar' : 'pode ver'} · {relativeTime(s.updatedAt)}
                      </span>
                    </span>
                  </button>
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}
